import type { Category, CategoryAttribute, ColorOption } from '../types/foundObject';

export type ConditionalAttributeLike = {
  type?: string;
  label?: string;
  depends_on?: string;
  conditional_labels?: Record<string, string>;
  conditional?: {
    dependsOn?: string;
    values?: Record<string, string[]>;
  };
  options?: string[] | Record<string, string[]> | ColorOption[];
};

export function categoryAttributesRecord(
  category: Pick<Category, 'attributes'> | null | undefined
): Record<string, CategoryAttribute & { use_as_filter?: boolean; disabled?: boolean }> {
  const raw = category?.attributes;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw as Record<string, CategoryAttribute & { use_as_filter?: boolean; disabled?: boolean }>;
}

export function isActiveCategoryAttribute(
  attribute: Pick<CategoryAttribute, 'disabled'> | undefined | null
): boolean {
  return !attribute?.disabled;
}

export function getActiveCategoryAttributeEntries(
  category: Pick<Category, 'attributes'> | null | undefined
): [string, CategoryAttribute & { use_as_filter?: boolean; disabled?: boolean }][] {
  return Object.entries(categoryAttributesRecord(category)).filter(([, def]) =>
    isActiveCategoryAttribute(def)
  );
}

export function getFilterableAttributeEntries(
  category: Pick<Category, 'attributes'> | null | undefined
): [string, CategoryAttribute & { use_as_filter?: boolean }][] {
  if (!category) return [];
  return getActiveCategoryAttributeEntries(category).filter(([, def]) =>
    Boolean(def?.use_as_filter)
  );
}

function normalizeOptions(
  options: CategoryAttribute['options'] | undefined
): string[] {
  if (!options) return [];
  if (Array.isArray(options)) {
    if (
      options.length > 0 &&
      typeof options[0] === 'object' &&
      options[0] !== null &&
      'label' in (options[0] as object)
    ) {
      return (options as { label?: string; value?: string; name?: string }[])
        .map((o) => o.label || o.value || o.name || '')
        .filter(Boolean);
    }
    return options as string[];
  }
  return Object.keys(options);
}

function optionsRecordFromAttribute(
  attribute: Pick<ConditionalAttributeLike, 'options'>,
): Record<string, string[]> | null {
  if (
    typeof attribute.options === 'object' &&
    attribute.options !== null &&
    !Array.isArray(attribute.options)
  ) {
    return attribute.options as Record<string, string[]>;
  }
  return null;
}

export function resolveConditionalDependsOnValue(
  attributeKey: string,
  attribute: ConditionalAttributeLike,
  dependentValues: Record<string, string>,
): string {
  const optionsRecord = optionsRecordFromAttribute(attribute);
  if (!optionsRecord) return '';

  if (attribute.depends_on) {
    const parentValue = dependentValues[attribute.depends_on];
    if (parentValue && optionsRecord[parentValue]) {
      return parentValue;
    }
    return parentValue || '';
  }

  if (attribute.conditional?.dependsOn) {
    const parentValue = dependentValues[attribute.conditional.dependsOn];
    if (parentValue && optionsRecord[parentValue]) {
      return parentValue;
    }
    return parentValue || '';
  }

  if (attributeKey === 'size' || attributeKey === 'subtype') {
    const v = dependentValues.category_type || dependentValues.clothing_type || '';
    return v && optionsRecord[v] ? v : v;
  }

  if (attributeKey === 'model' || attributeKey === 'modele') {
    const v = dependentValues.brand || dependentValues.marque || '';
    return v && optionsRecord[v] ? v : v;
  }

  for (const depValue of Object.values(dependentValues)) {
    if (depValue && optionsRecord[depValue]) {
      return depValue;
    }
  }

  return '';
}

export function resolveConditionalSelectLabel(
  attribute: Pick<CategoryAttribute, 'conditional_labels'> & { label?: string },
  dependsOnValue: string,
): string {
  if (dependsOnValue && attribute.conditional_labels?.[dependsOnValue]) {
    return attribute.conditional_labels[dependsOnValue];
  }
  return attribute.label ?? '';
}

export function resolveCategoryAttributeDisplayLabel(
  attributeKey: string,
  categoryAttributes: Record<string, ConditionalAttributeLike> | null | undefined,
  objectAttributes?: Record<string, unknown> | null,
): string {
  const def = categoryAttributes?.[attributeKey];
  if (!def) {
    return attributeKey.replace(/_/g, ' ');
  }
  if (def.type === 'conditional_select' && objectAttributes) {
    const dependsOnValue = resolveConditionalDependsOnValue(
      attributeKey,
      def,
      Object.fromEntries(
        Object.entries(objectAttributes).map(([k, v]) => [k, v == null ? '' : String(v)]),
      ),
    );
    return resolveConditionalSelectLabel(def, dependsOnValue);
  }
  return def.label || attributeKey.replace(/_/g, ' ');
}

export function resolveConditionalSelectOptions(
  attributeKey: string,
  attribute: CategoryAttribute,
  dependentValues: Record<string, string>,
): string[] {
  const optionsRecord = optionsRecordFromAttribute(attribute);
  if (!optionsRecord) return [];

  const dependsOnValue = resolveConditionalDependsOnValue(attributeKey, attribute, dependentValues);
  if (dependsOnValue && optionsRecord[dependsOnValue]) {
    return optionsRecord[dependsOnValue];
  }
  return [];
}

export function attributeSelectOptions(
  key: string,
  def: CategoryAttribute,
  dependentValues: Record<string, string> = {}
): string[] {
  if (def.type === 'conditional_select') {
    return resolveConditionalSelectOptions(key, def, dependentValues);
  }
  return normalizeOptions(def.options);
}

export function pruneDependentAttributeFilters(
  filters: Record<string, string>,
  category: Pick<Category, 'attributes'> | null | undefined
): Record<string, string> {
  const defs = categoryAttributesRecord(category);
  const next = { ...filters };
  let changed = false;

  for (const [childKey, childDef] of Object.entries(defs)) {
    if (childDef.type !== 'conditional_select') continue;
    const current = next[childKey];
    if (!current) continue;
    const opts = attributeSelectOptions(childKey, childDef, next);
    if (!opts.includes(current)) {
      delete next[childKey];
      changed = true;
    }
  }

  return changed ? next : filters;
}
